/* eslint-disable no-unused-vars */
import {
  Button,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Typography,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import PropTypes from 'prop-types';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SearchableAutocomplete from '../SearchableAutocomplete';
import TableLoader from '../TableLoader';
import listingStyle from '../../theme/styles/pages/Listing';
import { videosList } from '../../services/List';
import SnackbarMessage from '../SnackbarMessage';
import ConfirmDialog from '../ConfirmDialog';
import RoutePaths from '../../config/Routes';
import { assignVideos, getChannelVideos, removeAssignedVideo } from '../../services/Channels';

const useStyles = makeStyles(listingStyle);

const ChannelVideosPage = ({ channel }) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = channel;

  const [rows, setRows] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [reloadRows, setReloadRows] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [videos, setVideos] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [snackbarMeesage, setSnackbarMeesage] = useState({
    show: false,
    type: '',
    message: '',
  });

  const [actionId, setActionId] = useState(0);

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [assignConfirmDialog, setAssignConfirmDialog] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  useEffect(() => {
    getChannelVideos(id, activePage)
      .then((res) => {
        setRows(res.data.rows);
        setTotalPages(res.data.totalPages);
        setDataLoaded(true);
      })
      .catch(() => {
        setSnackbarMeesage({
          ...snackbarMeesage,
          message: 'Error in loading the data.',
          type: 'error',
          show: true,
        });
        setRows([]);
        setDataLoaded(true);
      });
  }, [reloadRows]);

  const loadVideos = (inputText, callBack) => {
    videosList(inputText, 1).then((res) => {
      callBack(res.data.rows);
    });
  };

  const onVideoSelectionChange = (values) => {
    setVideos(values);
  };

  const assignVideo = () => {
    setSnackbarMeesage({
      message: '',
      type: '',
      show: false,
    });
    assignVideos(id, { videos })
      .then((res) => {
        const filterFalse = res.data.filter((v) => v.status === false);
        if (filterFalse.length > 0) {
          setSnackbarMeesage({
            ...snackbarMeesage,
            message: filterFalse.map((v) => v.message).join(', '),
            type: 'error',
            show: true,
          });
          setReloadRows(!reloadRows);
          setProcessing(false);
          setVideos([]);
        } else {
          setReloadRows(!reloadRows);
          setProcessing(false);
          setVideos([]);
        }
        setAssignConfirmDialog(false);
      })
      .catch(({ response }) => {
        setSnackbarMeesage({
          ...snackbarMeesage,
          message: response.data.message,
          type: 'error',
          show: true,
        });
        setProcessing(false);
        setVideos([]);
        setAssignConfirmDialog(false);
      });
  };

  const handleAssign = () => {
    if (videos.some((item) => item.isDiffFilmmaker)) {
      setAssignConfirmDialog(true);
    } else {
      assignVideo();
    }
  };

  const deleteRecord = () => {
    setSnackbarMeesage({
      message: '',
      type: '',
      show: false,
    });
    setDeleteProcessing(true);
    removeAssignedVideo(id, actionId)
      .then(() => {
        setDeleteProcessing(false);
        setConfirmDialog(false);
        setReloadRows(!reloadRows);
      })
      .catch(({ response }) => {
        setSnackbarMeesage({
          ...snackbarMeesage,
          message: response.data.message,
          type: 'error',
          show: true,
        });
        setDeleteProcessing(false);
      });
  };

  const channelVideosOrder = () => {
    const url = RoutePaths.ORDER_CHANNEL_VIDEOS.replace(':id', id);
    history.push(`${url}`);
  };

  const handleCopyChannelLink = async () => {
    setSnackbarMeesage({ ...snackbarMeesage, show: false });

    const url = channel.filmmakerChannelShortlink;
    navigator.clipboard.writeText(url);
    setTimeout(() => {
      setSnackbarMeesage({
        ...snackbarMeesage,
        show: true,
        type: 'success',
        message: 'Link copied successfully.',
      });
    }, 100);
  };

  return (
    <div>
      <div className={classes.filterToolbar}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center">
              <Box width={300}>
                <SearchableAutocomplete
                  loadOptions={loadVideos}
                  onChange={onVideoSelectionChange}
                  value={videos}
                  label="Videos"
                  size="small"
                />
              </Box>

              <div className={` ${classes.mb18} ${classes.assignButton}`}>
                <Box whiteSpace="nowrap">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={processing ? <CircularProgress size={25} /> : <AddIcon />}
                    className={classes.addNewBtn}
                    onClick={handleAssign}
                    disabled={processing || videos.length === 0}
                    sx={{ whiteSpace: 'nowrap' }} // ✅ prevent wrapping
                  >
                    Assign To Channel
                  </Button>
                </Box>
              </div>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Button
                variant="contained"
                color="primary"
                className={classes.addNewBtn}
                onClick={channelVideosOrder}
                style={{ marginRight: '10px' }}
              >
                MANAGE PROGRAM ORDER
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.addNewBtn}
                onClick={handleCopyChannelLink}
                style={{ marginRight: '10px' }}
              >
                COPY MY CHANNEL PROMO LINK
              </Button>
            </Box>
            <Box justifyContent="flex-end" display="flex" mr={2} mt={0.5}>
              <Typography variant="caption" style={{ whiteSpace: 'nowrap' }}>
                User clicks: {channel.shortlinkClicks}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </div>

      <TableContainer>
        <Table className={classes.tableData} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell width="50%">Video Name</TableCell>
              <TableCell align="right" width="15%">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!dataLoaded && <TableLoader cols={2} />}
            {dataLoaded && rows.length === 0 && (
              <TableCell
                align="center"
                size="medium"
                colSpan={8}
                className={classes.noRecordFoundText}
              >
                No records found
              </TableCell>
            )}
            {dataLoaded &&
              rows.map((row) => (
                <TableRow key={`st-${row.id}`}>
                  <TableCell>{row.title}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="delete"
                      className={classes.deleteBtn}
                      onClick={() => {
                        setDeleteProcessing(false);
                        setConfirmDialog(true);
                        setActionId(row.videoId);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dataLoaded && totalPages > 1 && (
        <Pagination
          count={totalPages}
          showFirstButton
          showLastButton
          className={classes.tablePagination}
          onChange={(_, pageNumber) => {
            setActivePage(pageNumber);
            setReloadRows(!reloadRows);
          }}
          page={activePage}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title="Delete Assigned Video"
          message="Do you want to delete this record? This action can not be undone."
          onClose={() => {
            setActionId(null);
            setConfirmDialog(false);
          }}
          onApprove={deleteRecord}
          approveProcessing={deleteProcessing}
        />
      )}

      {assignConfirmDialog && (
        <ConfirmDialog
          title="Assigned Video"
          message="This content is owned by another filmmaker. You may add it to your channel, but you will not earn any revenue from views on this content."
          onClose={() => {
            setAssignConfirmDialog(false);
          }}
          onApprove={assignVideo}
          approveButtonText="Add to My Channel"
          closeButtonText="Back"
          approveProcessing={deleteProcessing}
        />
      )}

      {snackbarMeesage.show && <SnackbarMessage {...snackbarMeesage} />}
    </div>
  );
};

export default ChannelVideosPage;

ChannelVideosPage.propTypes = {
  channel: PropTypes.objectOf.isRequired,
};
